import React, { useMemo, useState } from "react";

function num(v) {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function fmtAxis(n, suffix = "") {
  if (!Number.isFinite(n)) return "0";
  const abs = Math.abs(n);
  const s = abs >= 100 ? abs.toFixed(0) : abs.toFixed(abs >= 10 ? 1 : 2);
  return `${n < 0 ? "-" : ""}${s}${suffix}`;
}

function fmtDateLabel(dateStr) {
  if (!dateStr) return "";
  const parts = String(dateStr).split("-");
  if (parts.length >= 3) return `${parts[1]}-${parts[2]}`;
  return dateStr;
}

function shouldShowXLabel(index, total) {
  if (total <= 10) return true;
  const step = Math.ceil(total / 8);
  return index % step === 0 || index === total - 1;
}

function ChartTooltip({ point, lines }) {
  if (!point) return null;
  return (
    <div
      className="options-pnl-chart__tooltip"
      style={{ left: point.x, top: point.y }}
    >
      <div className="options-pnl-chart__tooltip-date">{point.date}</div>
      {lines.map((line) => (
        <div key={line.label}>{line.label}: {line.value}</div>
      ))}
    </div>
  );
}

export function OptionsDailyPnlChart({ data = [], title = "Daily Account PNL" }) {
  const [hover, setHover] = useState(null);
  const series = useMemo(
    () => (Array.isArray(data) ? data.map((d) => ({ date: d.date, value: num(d.value) })) : []),
    [data]
  );

  const W = 520;
  const H = 220;
  const pad = { t: 16, r: 12, b: 28, l: 44 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;

  const { minY, maxY, bars, yTicks, zeroY } = useMemo(() => {
    if (!series.length) {
      return { minY: -1, maxY: 1, bars: [], yTicks: [-1, 0, 1], zeroY: pad.t + innerH / 2 };
    }
    const vals = series.map((s) => s.value);
    let min = Math.min(0, ...vals);
    let max = Math.max(0, ...vals);
    if (min === max) {
      min -= 1;
      max += 1;
    }
    const span = max - min || 1;
    const zero = pad.t + ((max - 0) / span) * innerH;
    const step = series.length > 1 ? innerW / series.length : innerW;
    const barW = Math.max(4, Math.min(24, step * 0.55));
    const mapped = series.map((s, i) => {
      const x = pad.l + i * step + (step - barW) / 2;
      const yVal = pad.t + ((max - s.value) / span) * innerH;
      const y0 = zero;
      const top = Math.min(yVal, y0);
      const height = Math.max(2, Math.abs(yVal - y0));
      return { ...s, x, y: top, w: barW, h: height, cx: x + barW / 2 };
    });
    const ticks = [max, max / 2, 0, min / 2, min].filter((v, i, arr) => arr.indexOf(v) === i);
    return { minY: min, maxY: max, bars: mapped, yTicks: ticks, zeroY: zero };
  }, [series, innerH, innerW, pad.l, pad.t]);

  return (
    <div className="options-pnl-chart h-100">
      <div className="options-pnl-chart__title">{title}</div>
      <div className="options-pnl-chart__wrap">
        <svg viewBox={`0 0 ${W} ${H}`} className="options-pnl-chart__svg" role="img" aria-label={title}>
          {yTicks.map((tick) => {
            const y = pad.t + ((maxY - tick) / (maxY - minY || 1)) * innerH;
            return (
              <g key={tick}>
                <line x1={pad.l} y1={y} x2={W - pad.r} y2={y} className="options-pnl-chart__grid" />
                <text x={pad.l - 6} y={y + 4} textAnchor="end" className="options-pnl-chart__axis">
                  {fmtAxis(tick)}
                </text>
              </g>
            );
          })}
          <line x1={pad.l} y1={zeroY} x2={W - pad.r} y2={zeroY} className="options-pnl-chart__zero" />
          {bars.map((b) => (
            <rect
              key={b.date}
              x={b.x}
              y={b.y}
              width={b.w}
              height={b.h}
              className={b.value >= 0 ? "options-pnl-chart__bar--pos" : "options-pnl-chart__bar--neg"}
              onMouseEnter={() => setHover({ date: b.date, x: `${((b.cx / W) * 100).toFixed(1)}%`, y: 8, value: b.value })}
              onMouseLeave={() => setHover(null)}
            />
          ))}
          {bars.map((b, i) => (
            shouldShowXLabel(i, bars.length) ? (
              <text key={`${b.date}-x`} x={b.cx} y={H - 8} textAnchor="middle" className="options-pnl-chart__axis">
                {fmtDateLabel(b.date)}
              </text>
            ) : null
          ))}
        </svg>
        <ChartTooltip
          point={hover}
          lines={hover ? [{ label: "Daily Account PNL", value: `${num(hover.value).toFixed(4)} USDT` }] : []}
        />
      </div>
    </div>
  );
}

export function OptionsCumulativePnlChart({ usdtData = [], pctData = [], title = "Cumulative PNL %" }) {
  const [hover, setHover] = useState(null);
  const usdt = useMemo(
    () => (Array.isArray(usdtData) ? usdtData.map((d) => ({ date: d.date, value: num(d.value) })) : []),
    [usdtData]
  );
  const pct = useMemo(
    () => (Array.isArray(pctData) ? pctData.map((d) => ({ date: d.date, value: num(d.value) })) : []),
    [pctData]
  );
  const dates = usdt.length ? usdt.map((d) => d.date) : pct.map((d) => d.date);

  const W = 520;
  const H = 220;
  const pad = { t: 16, r: 44, b: 28, l: 44 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;

  const chart = useMemo(() => {
    if (!dates.length) {
      return { bars: [], line: "", pctLine: "", yTicks: [0], pctTicks: [0], minY: 0, maxY: 1, minP: 0, maxP: 1 };
    }
    const uVals = dates.map((d, i) => usdt[i]?.value ?? 0);
    const pVals = dates.map((d, i) => pct[i]?.value ?? 0);
    let min = Math.min(0, ...uVals);
    let max = Math.max(0, ...uVals);
    if (min === max) { min -= 1; max += 1; }
    let minP = Math.min(0, ...pVals);
    let maxP = Math.max(0, ...pVals);
    if (minP === maxP) { minP -= 1; maxP += 1; }
    const span = max - min || 1;
    const spanP = maxP - minP || 1;
    const step = dates.length > 1 ? innerW / dates.length : innerW;
    const barW = Math.max(4, Math.min(24, step * 0.55));
    const bars = dates.map((date, i) => {
      const value = uVals[i];
      const x = pad.l + i * step + (step - barW) / 2;
      const yVal = pad.t + ((max - value) / span) * innerH;
      const y0 = pad.t + ((max - 0) / span) * innerH;
      const top = Math.min(yVal, y0);
      const height = Math.max(2, Math.abs(yVal - y0));
      return { date, value, pct: pVals[i], x, y: top, w: barW, h: height, cx: x + barW / 2 };
    });
    const linePts = bars.map((b, i) => {
      const p = pVals[i];
      const y = pad.t + ((maxP - p) / spanP) * innerH;
      const x = pad.l + i * step + step / 2;
      return `${x},${y}`;
    }).join(" ");
    const pctLinePts = bars.map((b, i) => {
      const p = pVals[i];
      const y = pad.t + ((maxP - p) / spanP) * innerH;
      const x = pad.l + i * step + step / 2;
      return { x, y, date: b.date, usdt: b.value, pct: p };
    });
    return {
      bars,
      line: linePts,
      pctLinePts,
      yTicks: [max, max / 2, 0, min / 2, min],
      pctTicks: [maxP, maxP / 2, 0, minP / 2, minP],
      minY: min,
      maxY: max,
      minP,
      maxP,
    };
  }, [dates, usdt, pct, innerH, innerW, pad.l, pad.t]);

  return (
    <div className="options-pnl-chart h-100">
      <div className="options-pnl-chart__title">{title}</div>
      <div className="options-pnl-chart__wrap">
        <svg viewBox={`0 0 ${W} ${H}`} className="options-pnl-chart__svg" role="img" aria-label={title}>
          {chart.yTicks.map((tick) => {
            const y = pad.t + ((chart.maxY - tick) / (chart.maxY - chart.minY || 1)) * innerH;
            return (
              <g key={`y-${tick}`}>
                <line x1={pad.l} y1={y} x2={W - pad.r} y2={y} className="options-pnl-chart__grid" />
                <text x={pad.l - 6} y={y + 4} textAnchor="end" className="options-pnl-chart__axis">
                  {fmtAxis(tick)}
                </text>
              </g>
            );
          })}
          {chart.pctTicks.map((tick) => {
            const y = pad.t + ((chart.maxP - tick) / (chart.maxP - chart.minP || 1)) * innerH;
            return (
              <text key={`p-${tick}`} x={W - pad.r + 6} y={y + 4} textAnchor="start" className="options-pnl-chart__axis">
                {fmtAxis(tick, "%")}
              </text>
            );
          })}
          {chart.bars.map((b) => (
            <rect
              key={b.date}
              x={b.x}
              y={b.y}
              width={b.w}
              height={b.h}
              className="options-pnl-chart__bar--cum"
              onMouseEnter={() => setHover({
                date: b.date,
                x: `${((b.cx / W) * 100).toFixed(1)}%`,
                y: 8,
                usdt: b.value,
                pct: b.pct,
              })}
              onMouseLeave={() => setHover(null)}
            />
          ))}
          {chart.line && (
            <polyline points={chart.line} fill="none" className="options-pnl-chart__line" />
          )}
          {chart.pctLinePts?.map((pt) => (
            <circle
              key={pt.date}
              cx={pt.x}
              cy={pt.y}
              r={3}
              className="options-pnl-chart__dot"
            />
          ))}
          {chart.bars.map((b, i) => (
            shouldShowXLabel(i, chart.bars.length) ? (
              <text key={`${b.date}-x`} x={b.cx} y={H - 8} textAnchor="middle" className="options-pnl-chart__axis">
                {fmtDateLabel(b.date)}
              </text>
            ) : null
          ))}
        </svg>
        <ChartTooltip
          point={hover}
          lines={hover ? [
            { label: "Cumulative PNL", value: `${num(hover.usdt).toFixed(4)} USDT` },
            { label: "Cumulative PNL %", value: `${num(hover.pct).toFixed(2)}%` },
          ] : []}
        />
      </div>
      <div className="options-pnl-chart__legend">
        <span><i className="options-pnl-chart__legend-bar" /> Cumulative PNL</span>
        <span><i className="options-pnl-chart__legend-line" /> Cumulative PNL %</span>
      </div>
    </div>
  );
}
